import { Component, Input, OnDestroy, OnInit, EventEmitter, Output, SimpleChanges } from '@angular/core';
import { Server } from '@flashvpn-io/web-core';
import { StatusService } from '../../services/status.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

@Component({
  selector: 'app-servers',
  templateUrl: './servers.component.html',
  styleUrls: ['./servers.component.css']
})
export class ServersComponent implements OnInit, OnDestroy {
  @Input() servers: Server[] = [];
  @Output() refreshServers = new EventEmitter<void>();

  public currentServer: Server | null = null;
  private currentServerSubscription = new Subscription();
  public isRefreshing: boolean = false;

  speed = 100;

  constructor(
    private statusService: StatusService,
    private translate: TranslateService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // 监听当前选中的服务器
    this.currentServerSubscription = this.statusService.getSelectedServer().subscribe(server => {
      console.log('getCurrentServer', server);
      this.currentServer = server;
    });
  }

  // 更新所有服务器的延迟
  private async updateLatency(): Promise<void> {
    if (!this.servers || this.servers.length === 0) return;

    console.log('Updating latency for servers:', this.servers);

    // 为每个服务器创建一个 Promise
    this.servers.forEach(async (s, index) => {
      try {
        const startTime = Date.now();
        await fetch(`https://${s.host}:${s.port}`, {
          mode: 'no-cors',
          cache: 'no-store'
        });
        const latency = Math.round(Date.now() - startTime);
        // 更新单个服务器的延迟
        this.servers[index] = {
          ...s,
          ping: s.ping,
          latency: `${latency} ms`
        };
      } catch (error) {
        // 更新单个服务器的延迟为失败状态
        this.servers[index] = {
          ...s,
          ping: s.ping,
          latency: 'failed'
        };
      }
    });
  }

  // 监听 servers 数组的变化
  ngOnChanges(changes: SimpleChanges): void {
    // 只在 servers 数组真正发生变化时更新
    if (changes['servers'] && changes['servers'].currentValue) {
      // 重置所有服务器的延迟值
      this.servers = this.servers.map(s => ({
        ...s,
        ping: s.ping,
        latency: ''
      }));
      this.updateLatency();
    }
  }

  getCountryFlag(serverName: string): string {
    const countryCodeMatch = serverName.match(/-(\w+)/i);
    const countryCode = countryCodeMatch ? countryCodeMatch[1].toUpperCase() : '';

    const flagMap: Record<string, string> = {
      'US': '🇺🇸',
      'JP': '🇯🇵',
      'SG': '🇸🇬',
      'HK': '🇭🇰',
      'TW': '🇹🇼',
      'DE': '🇩🇪',
    };

    return flagMap[countryCode] || '🇭🇰';
  }

  handleSort(type: 'asc' | 'desc'): void {
    // 按照延迟进行排序
    if (type === 'asc') {
      this.servers = [...this.servers].sort((a, b) => {
        // 如果任一服务器失败，将其排在后面
        if (a.latency === 'failed') return 1;
        if (b.latency === 'failed') return -1;

        const pingA = this.getPingValue(a);
        const pingB = this.getPingValue(b);
        return pingA - pingB;
      });
    } else {
      this.servers = [...this.servers].sort((a, b) => {
        // 如果任一服务器失败，将其排在最后
        if (a.latency === 'failed') return 1;
        if (b.latency === 'failed') return -1;

        const pingA = this.getPingValue(a);
        const pingB = this.getPingValue(b);
        // 倒序排序，延迟大的排在前面
        return pingB - pingA;
      });
    }
  }

  // 获取ping值的辅助方法
  private getPingValue(server: Server): number {
    if (server.latency === 'failed') return 0;
    const match = server.latency.match(/(\d+)\s*ms/);
    return match ? parseInt(match[1], 10) : 0;
  }

  handleRefresh(): void {
    console.log('刷新服务器列表');
    this.isRefreshing = true;

    // 触发父组件中的刷新方法
    this.refreshServers.emit();

    // 简单延迟模拟刷新动画
    setTimeout(() => {
      this.isRefreshing = false;
    }, 1000);
  }

  // 选择服务器
  selectServer(server: Server): void {
    this.statusService.setCurrentServer(server);
    this.back();
  }

  back() {
    // 重定向到/home路径
    this.router.navigate(['/home']);
  }

  // 获取延迟状态的颜色
  getLatencyColor(server: Server): string {
    // 如果延迟值还没有设置，返回灰色
    if (!server.latency) return 'grey';
    if (server.latency === 'failed') return 'red';
    const latency = this.getPingValue(server);
    if (latency < 300) return 'green';
    if (latency < 1000) return 'yellow';
    if (latency < 1600) return 'red';
    return 'red';
  }

  // 获取延迟显示文本
  getLatencyText(server: Server): string {
    if (!server.latency) return this.translate.instant('servers.testing');
    if (server.latency === 'failed') return this.translate.instant('servers.failed');
    return server.latency;
  }

  ngOnDestroy(): void {
    this.currentServerSubscription.unsubscribe();
  }
} 